'use client';

import { useState } from 'react';
import OfferPrice, { AnnualSavings } from './offer-price';
import { usePricing, PricingUnavailable } from './pricing-provider';
import CtaButton from '@/components/ui/cta-button/component';

export default function PricingTables() {
  const pricing = usePricing();
  const purchaseEnabled =
    !!pricing.config && pricing.config.clients.legacy_frontend.mode !== 'paused';
  const [annual, setAnnual] = useState<boolean>(true);

  return (
    <div>
      {!purchaseEnabled && <PricingUnavailable />}
      {/* Pricing toggle */}
      <div className="flex justify-center items-center space-x-7 md:space-x-5 mb-16">
        <div className="text-sm text-slate-600 font-semibold text-right min-w-[8rem]">
          Pay Monthly
        </div>
        <div className="form-switch shrink-0">
          <input
            type="checkbox"
            id="toggle"
            className="sr-only"
            checked={annual}
            onChange={() => setAnnual(!annual)}
          />
          <label className="bg-slate-300" htmlFor="toggle">
            <span className="bg-slate-300 border-8 border-blue-500" aria-hidden="true"></span>
            <span className="sr-only text-slate-600 font-semibold">Pay yearly</span>
          </label>
        </div>
        <div className="text-sm text-slate-600 font-semibold min-w-[8rem]">Pay Yearly</div>
      </div>

      <div className="max-w-sm mx-auto grid gap-8 lg:grid-cols-3 lg:gap-6 items-start lg:max-w-none pt-4">
        {/* Pricing table 1 */}
        <div
          className="relative flex flex-col h-full px-6 py-5 bg-white shadow-lg"
          data-aos="fade-up"
        >
          <div className="mb-4 pb-4 border-b border-slate-200">
            <div className="text-lg font-semibold text-zinc-800 mb-1">Basic</div>
            <div className="inline-flex items-baseline mb-3">
              <span className="h2 font-merriweather text-zinc-800 leading-[58px]">Free</span>
            </div>
            <div className="text-slate-600">
              Get started with our free plan.{' '}
              <strong className="font-medium">No credit card required</strong>.
            </div>
          </div>
          <div className="font-medium mb-4">Features include:</div>
          <ul className="text-slate-600 space-y-3 grow mb-6">
            <li className="flex items-center">
              <svg
                className="w-3 h-3 fill-current text-emerald-500 mr-3 shrink-0"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
              </svg>
              <span>Unlimited entries</span>
            </li>
            <li className="flex items-center">
              <svg
                className="w-3 h-3 fill-current text-emerald-500 mr-3 shrink-0"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
              </svg>
              <span>Mobile apps</span>
            </li>
            <li className="flex items-center">
              <svg
                className="w-3 h-3 fill-current text-emerald-500 mr-3 shrink-0"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
              </svg>
              <span>Basic journal covers and fonts</span>
            </li>
            <li className="flex items-center">
              <svg
                className="w-3 h-3 fill-current text-emerald-500 mr-3 shrink-0"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
              </svg>
              <span>Simple writing reminders</span>
            </li>
          </ul>
          <div className="p-3">
            <CtaButton text="Get started" size="small" />
          </div>
        </div>

        {/* Pricing table 2 */}
        <div
          className="relative flex flex-col h-full px-6 py-5 bg-white shadow-lg border-t-4 border-t-red-200"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          <div className="absolute -top-5 mx-auto left-1/2 -translate-x-1/2">
            <div className="inline-flex text-sm font-semibold py-1.5 px-[26px] text-brandred-500 bg-red-200 rounded-full">
              Most Popular
            </div>
          </div>
          <div className="mb-4 pb-4 border-b border-slate-200">
            <div className="text-lg font-semibold text-zinc-800 mb-1">Penzu Pro</div>
            <div className="inline-flex items-baseline mb-3 leading-[58px]">
              <OfferPrice tier={annual ? 'pro' : 'promonthly'} />
            </div>
            {annual && <AnnualSavings tier="pro" />}
            <div className="text-slate-600">
              Make it yours with customization, plus pro-active features to keep you writing.
            </div>
          </div>
          <div className="font-medium mb-4">Everything in Basic, plus:</div>
          <ul className="text-slate-600 space-y-3 grow mb-6">
            <li className="flex items-center">
              <svg
                className="w-3 h-3 fill-current text-emerald-500 mr-3 shrink-0"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
              </svg>
              <span>Encryption locking</span>
            </li>
            <li className="flex items-center">
              <svg
                className="w-3 h-3 fill-current text-emerald-500 mr-3 shrink-0"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
              </svg>
              <span>Custom journal covers, fonts &amp; colors</span>
            </li>
            <li className="flex items-center">
              <svg
                className="w-3 h-3 fill-current text-emerald-500 mr-3 shrink-0"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
              </svg>
              <span>Entry tagging</span>
            </li>
            <li className="flex items-center">
              <svg
                className="w-3 h-3 fill-current text-emerald-500 mr-3 shrink-0"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
              </svg>
              <span>PDF export</span>
            </li>
            <li className="flex items-center">
              <svg
                className="w-3 h-3 fill-current text-emerald-500 mr-3 shrink-0"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
              </svg>
              <span>Writing prompts</span>
            </li>
            <li className="flex items-center">
              <svg
                className="w-3 h-3 fill-current text-emerald-500 mr-3 shrink-0"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
              </svg>
              <span>Write via email (Penzu Post)</span>
            </li>
          </ul>
          <div className="p-3">
            <a
              className="btn-lg text-white bg-blue-500 hover:bg-blue-700 w-full group"
              aria-disabled={!purchaseEnabled}
              onClick={(event) => {
                if (!purchaseEnabled) event.preventDefault();
              }}
              href="/app/signup-pro"
            >
              Go Pro{' '}
              <span className="tracking-normal text-blue-300 group-hover:translate-x-0.5 transition-transform duration-150 ease-in-out ml-1">
                -&gt;
              </span>
            </a>
          </div>
        </div>

        {/* Pricing table 3 */}
        <div
          className="relative flex flex-col h-full px-6 py-5 bg-white shadow-lg"
          data-aos="fade-up"
        >
          <div className="mb-4 pb-4 border-b border-slate-200">
            <div className="text-lg font-semibold text-zinc-800 mb-1">Penzu Pro+</div>
            <div className="inline-flex items-baseline mb-3 leading-[58px]">
              <OfferPrice tier={annual ? 'proplus' : 'proplusmonthly'} />
            </div>
            {annual && <AnnualSavings tier="proplus" />}
            <div className="text-slate-600">The most powerful suite of tools for journaling.</div>
          </div>
          <div className="font-medium mb-4">Everything in Pro, plus:</div>
          <ul className="text-slate-600 space-y-3 grow mb-6">
            <li className="flex items-center">
              <svg
                className="w-3 h-3 fill-current text-emerald-500 mr-3 shrink-0"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
              </svg>
              <span>Premium handwritten fonts</span>
            </li>
            <li className="flex items-center">
              <svg
                className="w-3 h-3 fill-current text-emerald-500 mr-3 shrink-0"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
              </svg>
              <span>Fullscreen zen-mode</span>
            </li>
            <li className="flex items-center">
              <svg
                className="w-3 h-3 fill-current text-emerald-500 mr-3 shrink-0"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
              </svg>
              <span>Versions &amp; entry recovery</span>
            </li>
            <li className="flex items-center">
              <svg
                className="w-3 h-3 fill-current text-emerald-500 mr-3 shrink-0"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
              </svg>
              <span>Premium support</span>
            </li>
            <li className="flex items-center">
              <svg
                className="w-3 h-3 fill-current text-emerald-500 mr-3 shrink-0"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M10.28 2.28L3.989 8.575 1.695 6.28A1 1 0 00.28 7.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 2.28z" />
              </svg>
              <span>Military grade encryption locking</span>
            </li>
          </ul>
          <div className="p-3">
            <a
              className="btn-lg text-white bg-blue-500 hover:bg-blue-700 w-full group"
              aria-disabled={!purchaseEnabled}
              onClick={(event) => {
                if (!purchaseEnabled) event.preventDefault();
              }}
              href="/app/signup-pro"
            >
              Go Pro+{' '}
              <span className="tracking-normal text-blue-300 group-hover:translate-x-0.5 transition-transform duration-150 ease-in-out ml-1">
                -&gt;
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
